import { matchingVariant, variantImage, cartItems } from './ulti-commerce-core.js';

function initialize(container) {
  if (container.dataset.initialized) return;
  const product = JSON.parse(container.querySelector('[data-builder-product]').textContent);
  const overrides = JSON.parse(container.querySelector('[data-builder-images]').textContent);
  const gallery = JSON.parse(container.querySelector('[data-builder-gallery]').textContent);
  const form = container.querySelector('form');
  const selector = form.querySelector('[data-variant-id]');
  const allowedIds = new Set([...selector.options].map(option => option.value));
  const variants = product.variants.filter(variant => allowedIds.has(String(variant.id)));
  if (!variants.length) return;
  const controls = form.querySelector('[data-product-options]');
  const selectedConfiguration = container.querySelector('[data-selected-configuration]');
  const mobileConfiguration = container.querySelector('[data-mobile-configuration]');
  const mobileBuy = container.querySelector('[data-mobile-buy]');
  const status = form.querySelector('[data-builder-status]');
  const quantity = form.elements.quantity;
  const locale = document.documentElement.lang || 'en-SG';
  const money = cents => new Intl.NumberFormat(locale, { style: 'currency', currency: container.dataset.currency || 'SGD' }).format(cents / 100);
  const optionNames = product.options.map(option => typeof option === 'string' ? option : option.name);
  const variantFromURL = new URL(location.href).searchParams.get('variant');
  let selected = variants.find(v => String(v.id) === variantFromURL) || variants.find(v => String(v.id) === selector.value) || variants.find(v => v.available) || variants[0];
  let choices = [...selected.options];
  let busy = false;
  const allButtons = [];
  const video = container.querySelector('.ulti-product-video');
  const mainImage = container.querySelector('.ulti-selected-image');
  const videoButton = container.querySelector('[data-gallery-video]');
  let previousChoices;
  function element(tag, className, text) { const node = document.createElement(tag); node.className = className; if (text !== undefined) node.textContent = text; return node; }
  function resizedProductImage(url, width) {
    if (!url) return url;
    const imageURL = new URL(url, location.origin);
    if (imageURL.pathname.includes('/cdn/shop/files/')) imageURL.searchParams.set('width', width);
    return imageURL.toString();
  }
  function setImage(url) {
    if (!url) return;
    const image = container.querySelector('.ulti-selected-image');
    if (!image) return;
    video.pause(); video.hidden = true; image.hidden = false;
    image.removeAttribute('srcset');
    image.src = resizedProductImage(url, 1200);
    image.alt = `${product.title} - ${choices.join(' / ')}`;
  }
  function choiceButton(label, updates, image) {
    const button = element('button', image ? 'ulti-style-choice' : 'ulti-option-choice');
    button.type = 'button';
    button.setAttribute('role', 'radio');
    if (image) { const img = element('img', ''); img.src = resizedProductImage(image, 208); img.alt = `${product.title} — ${label}`; img.width = 104; img.height = 104; img.loading = 'lazy'; button.append(img); }
    button.append(element('span', '', label));
    button.addEventListener('click', () => { updates.forEach(([index, value]) => { choices[index] = value; }); update(true); });
    allButtons.push({ button, updates });
    return button;
  }
  function enableRadioKeyboard(row) {
    row.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const buttons = [...row.querySelectorAll('[role="radio"]:not(:disabled)')];
      if (!buttons.length) return;
      event.preventDefault();
      const current = Math.max(0, buttons.indexOf(document.activeElement));
      const target = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (current + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + buttons.length) % buttons.length;
      buttons[target].focus();
      buttons[target].click();
    });
  }
  const modelIndex = optionNames.findIndex(name => name.toLowerCase() === 'model');
  const colorIndex = optionNames.findIndex(name => /^(colou?r)$/.test(name.toLowerCase()));
  const combined = container.dataset.combine === 'true' && modelIndex >= 0 && colorIndex >= 0;
  if (combined) {
    const field = element('fieldset', 'ulti-option-field');
    field.append(element('legend', '', 'Model & Colour'));
    const row = element('div', 'ulti-style-choices');
    const models = [...new Set(variants.map(v => v.options[modelIndex]))].sort((a, b) => ['Ergo', 'Arc'].indexOf(a) - ['Ergo', 'Arc'].indexOf(b));
    for (const model of models) for (const color of new Set(variants.map(v => v.options[colorIndex]))) {
      const match = variants.find(v => v.options[modelIndex] === model && v.options[colorIndex] === color);
      if (match) row.append(choiceButton(`${model} / ${color}`, [[modelIndex, model], [colorIndex, color]], variantImage(product, match, overrides)));
    }
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', 'Model and colour');
    enableRadioKeyboard(row);
    field.append(row);
    controls.append(field);
  }
  optionNames.forEach((name, index) => {
    if (combined && [modelIndex, colorIndex].includes(index)) return;
    if (product.options.length === 1 && name === 'Title' && variants.length === 1) return;
    const field = element('fieldset', 'ulti-option-field');
    field.append(element('legend', '', name));
    const row = element('div', 'ulti-option-choices');
    for (const value of new Set(variants.map(v => v.options[index]))) row.append(choiceButton(value, [[index, value]]));
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', name);
    enableRadioKeyboard(row);
    field.append(row);
    controls.append(field);
  });
  function update(changeURL = false) {
    selected = matchingVariant(variants, choices);
    selector.value = selected ? String(selected.id) : '';
    for (const { button, updates } of allButtons) {
      const checked = updates.every(([index, value]) => choices[index] === value);
      button.setAttribute('aria-checked', String(checked));
      button.tabIndex = checked ? 0 : -1;
      button.disabled = busy;
      const candidate = [...choices]; updates.forEach(([index, value]) => { candidate[index] = value; });
      const available = matchingVariant(variants, candidate)?.available;
      button.classList.toggle('ulti-option-unavailable', !available);
      button.title = available ? '' : 'Unavailable in this configuration';
    }
    const stock = container.querySelector('[data-product-stock]');
    stock.textContent = selected ? selected.available ? 'Available to order' : 'Currently unavailable' : 'Configuration unavailable';
    stock.dataset.available = String(Boolean(selected?.available));
    const selectedLabel = selected ? selected.title : 'Configuration unavailable';
    container.querySelector('[data-product-price]').textContent = selected ? money(selected.price) : 'Select an available configuration';
    container.querySelector('[data-product-mobile-price]').textContent = selected ? money(selected.price) : 'Unavailable';
    if (selectedConfiguration) selectedConfiguration.textContent = `Selected: ${selectedLabel}`;
    if (mobileConfiguration) mobileConfiguration.textContent = selectedLabel;
    const compare = container.querySelector('[data-product-compare]');
    compare.hidden = !selected || !(selected.compare_at_price > selected.price);
    compare.textContent = selected?.compare_at_price ? money(selected.compare_at_price) : '';
    container.querySelectorAll('[data-purchase]').forEach(button => { button.disabled = busy || !selected?.available || selected.requires_selling_plan || product.requires_selling_plan; });
    const rule = selected?.quantity_rule || { min: 1, increment: 1 };
    quantity.min = rule.min || 1; quantity.step = rule.increment || 1;
    if (rule.max) quantity.max = rule.max; else quantity.removeAttribute('max');
    if (previousChoices !== JSON.stringify(choices)) {
      setImage(gallery.image || variantImage(product, selected || { options: choices }, overrides));
      previousChoices = JSON.stringify(choices);
    }
    updateTotal();
    if (changeURL && document.body.dataset.localPreview !== 'true') {
      const url = new URL(location.href);
      if (selected) url.searchParams.set('variant', selected.id); else url.searchParams.delete('variant');
      history.replaceState(null, '', url);
    }
  }
  function updateTotal() {
    const extras = [...form.querySelectorAll('[data-addon-id]:checked')];
    const total = form.querySelector('[data-product-total]');
    total.hidden = !selected || !extras.length;
    if (selected) total.textContent = `Total with selected add-ons: ${money((selected.price + extras.reduce((sum, input) => sum + Number(input.dataset.price), 0)) * (Number(quantity.value) || 1))}`;
  }
  form.addEventListener('change', updateTotal);
  form.querySelectorAll('[data-addon-variant]').forEach(select => select.addEventListener('change', () => {
    const option = select.selectedOptions[0];
    const checkbox = document.getElementById(select.dataset.checkbox);
    checkbox.dataset.addonId = option.value;
    checkbox.dataset.price = option.dataset.price;
    checkbox.dataset.available = option.dataset.available;
    checkbox.disabled = option.dataset.available !== 'true';
    if (checkbox.disabled) checkbox.checked = false;
    select.closest('.ulti-product-extra').querySelector('[data-addon-price]').textContent = money(Number(option.dataset.price));
    updateTotal();
  }));
  quantity.addEventListener('input', updateTotal);
  const thumbnails = [...container.querySelectorAll('[data-gallery-image]')];
  if (gallery.image && thumbnails[0]) { thumbnails[0].dataset.galleryImage = gallery.image; thumbnails[0].querySelector('img').src = resizedProductImage(gallery.image, 240); }
  let limit = Number(gallery.limit) || thumbnails.length;
  if (!Number(gallery.limit) && thumbnails.length > 4) {
    const description = new DOMParser().parseFromString(product.description || '', 'text/html').body;
    description.querySelectorAll('p,li,div,h1,h2,h3,h4,h5,h6,br').forEach(node => node.append('\n'));
    const text = description.textContent.replace(/\s+/g, ' ').trim();
    const parts = text.split(/(?:^|(?<=[.)]\s))([A-Z][A-Za-z0-9 '\u2019/\-]{2,40}\s?:\s)/g).filter(part => part.trim());
    if (parts.length >= 4 && parts.length % 2 === 0 && parts.some((part, index) => index % 2 === 0 && index / 2 < thumbnails.length - 4 && !/^notes?:/i.test(part) && !/got you covered/i.test(part) && !/trademark/i.test(parts[index + 1]))) limit = 4;
  }
  thumbnails.forEach((button, index) => { button.hidden = index >= limit; button.addEventListener('click', () => {
    setImage(button.dataset.galleryImage); videoButton.setAttribute('aria-pressed', 'false');
    container.querySelectorAll('[data-gallery-image]').forEach(other => other.setAttribute('aria-pressed', String(other === button)));
  }); });
  function showVideo(play = true) {
    if (!gallery.video) return;
    video.src ||= gallery.video;
    video.hidden = false; if (mainImage) mainImage.hidden = true;
    thumbnails.forEach(button => button.setAttribute('aria-pressed', 'false'));
    videoButton.setAttribute('aria-pressed', 'true');
    if (play) video.play().catch(() => {});
  }
  videoButton.hidden = !gallery.video;
  videoButton.addEventListener('click', () => showVideo());
  container.querySelector('.ulti-product-thumbnails').hidden = thumbnails.length < 2 && !gallery.video;
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy || !form.reportValidity()) return;
    status.textContent = '';
    let sent = false;
    try {
      const extras = [...form.querySelectorAll('[data-addon-id]:checked')].map(input => ({ id: Number(input.dataset.addonId), available: input.dataset.available === 'true' }));
      const items = cartItems(selected, Number(quantity.value), extras, product.id);
      if (document.body.dataset.localPreview === 'true') throw new Error('Local preview: no cart changes or checkout requests were sent.');
      busy = true; update();
      form.setAttribute('aria-busy', 'true');
      status.textContent = 'Adding your setup...';
      sent = true;
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Your setup could not be added.');
      status.textContent = 'Your setup was added to the cart.';
      if (event.submitter?.dataset.purchase === 'checkout') location.assign(`${root}checkout`);
      else document.dispatchEvent(new CustomEvent('ulti:cart-added', { detail: data }));
    } catch (error) {
      status.textContent = error.message + (sent ? ' Check your cart before trying again.' : '');
      if (sent) document.dispatchEvent(new CustomEvent('ulti:cart-refresh'));
    } finally { busy = false; form.removeAttribute('aria-busy'); update(); }
  });
  container.dataset.initialized = 'true';
  controls.hidden = false;
  form.querySelector('.ulti-product-extras').hidden = false;
  form.querySelector('.ulti-variant-fallback').hidden = true;
  form.querySelector('[data-purchase="checkout"]').hidden = false;
  if (mobileBuy && 'IntersectionObserver' in window) {
    let builderVisible = false;
    let actionsVisible = true;
    const showMobileBuy = () => { mobileBuy.dataset.visible = String(builderVisible && !actionsVisible); };
    new IntersectionObserver(entries => { builderVisible = entries[0].isIntersecting; showMobileBuy(); }, { threshold: 0.05 }).observe(container);
    new IntersectionObserver(entries => { actionsVisible = entries[0].isIntersecting; showMobileBuy(); }, { threshold: 0.1 }).observe(form.querySelector('.ulti-product-actions'));
  }
  update(Boolean(variantFromURL && !variants.some(variant => String(variant.id) === variantFromURL)));
  if (mainImage) video.poster = mainImage.currentSrc || mainImage.src;
}
function scan(root = document) { root.querySelectorAll('[data-ulti-builder]').forEach(initialize); }
scan();
document.addEventListener('shopify:section:load', event => scan(event.target));
